/**
 * Attribute Selector Utility
 *
 * Builds CSS selectors from module config to support primary attributes + aliases.
 * Enables easy rebranding and backward compatibility.
 */

interface AttributeSelectorConfig {
  module?: string;
  attributes?: {
    elements?: {
      [key: string]: {
        primary: string;
        aliases?: string[];
      };
    };
  };
}

/**
 * Builds a CSS selector from config attribute patterns
 */
export function getSelector(config: AttributeSelectorConfig, elementKey: string): string {
  const element = config.attributes?.elements?.[elementKey];
  if (!element) {
    console.warn(`[attributeSelector] No config for element: ${elementKey}`);
    return '';
  }

  const selectors = [
    `[${element.primary}]`,
    ...(element.aliases || []).map((alias: string) => `[${alias}]`),
  ];

  return selectors.join(', ');
}

/**
 * Gets all matching elements for a given element type
 */
export function querySelectorAll(
  config: AttributeSelectorConfig,
  elementKey: string,
  root: Document | Element = document
): NodeListOf<Element> {
  const selector = getSelector(config, elementKey);
  return root.querySelectorAll(selector);
}

/**
 * Gets first matching element for a given element type
 */
export function querySelector(
  config: AttributeSelectorConfig,
  elementKey: string,
  root: Document | Element = document
): Element | null {
  const selector = getSelector(config, elementKey);
  return root.querySelector(selector);
}

/**
 * Logs deprecation warning if using non-primary attributes
 */
export function checkDeprecated(
  element: Element | null,
  config: AttributeSelectorConfig,
  elementKey: string
): void {
  if (!element) return;

  const elementConfig = config.attributes.elements[elementKey];
  const elementAttrs = element.getAttributeNames();

  elementConfig.aliases?.forEach((alias: string) => {
    const attrName = alias.split('=')[0].replace(/[\[\]']/g, '');
    if (elementAttrs.includes(attrName)) {
      console.warn(
        `[${config.module}] Using deprecated attribute "${alias}". ` +
          `Please use "${elementConfig.primary}" instead. ` +
          `Support for deprecated attributes will be removed in v3.0.0`
      );
    }
  });
}
